import styled, { css, keyframes } from "styled-components";
import { tokens as T, surface as surf, hairline as hair } from "../../styles/designTokens";

/**
 * Tela de Atividades — layout com navegação em pilha (inspiração iOS) sobre a
 * linguagem visual do Vidal (mesma da PLCom / Reforma Tributária):
 * superfícies translúcidas + bordas hairline, eyebrows mono, números
 * tabulares, acento esmeralda com fundo suave + anel nos estados ativos.
 */

// ─── Tokens derivados do tema (API usada também inline nos componentes) ─────
const dk = (t) => t.body !== "#fafbfc";
const label = (t) => t.text;
const label2 = (t) => (dk(t) ? "rgba(229,231,235,0.55)" : "rgba(15,23,42,0.55)");
const label3 = (t) => (dk(t) ? "rgba(229,231,235,0.28)" : "rgba(15,23,42,0.28)");
const fill = (t) => surf(t, 2);
const fill2 = (t) => surf(t, 1);
const separator = (t) => hair(t);
const surface = (t) => surf(t, 0);
const surface2 = (t) => surf(t, 2);
const elevated = () => "none";
const glass = (t) => (dk(t) ? "rgba(13,15,20,0.78)" : "rgba(250,251,252,0.82)");
// Fundo opaco para camadas sobrepostas (folha lateral)
const solid = (t) => (dk(t) ? "#111318" : "#fafbfc");

export const tokens = { dk, label, label2, label3, fill, fill2, separator, surface, surface2, elevated, glass, solid };

const mono = css`
  font-family: ${T.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
`;
const eyebrow = css`
  ${mono};
  font-size: ${T.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;
const cardBase = (theme) => css`
  background: ${surf(theme, 0)};
  border: 1px solid ${hair(theme)};
  border-radius: ${T.radiusLg};
  transition: border-color ${T.durBase} ${T.easeOut}, background ${T.durBase} ${T.easeOut},
    transform ${T.durBase} ${T.easeOut}, box-shadow ${T.durBase} ${T.easeOut};
`;
const cardHover = (theme) => css`
  border-color: ${hair(theme, true)};
  background: ${surf(theme, 1)};
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const slideIn = keyframes`
  from { transform: translateX(104%); }
  to   { transform: translateX(0); }
`;
const pushIn = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;
const spin = keyframes`from { transform: rotate(0); } to { transform: rotate(360deg); }`;

// ─── Página ─────────────────────────────────────────────────────────────────
export const Page = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  padding: 0 2em 3em;
  position: relative;
  color: ${({ theme }) => theme.text};
  animation: ${fadeIn} ${T.durSlow} ${T.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${T.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${T.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  .spin { animation: ${spin} 1s linear infinite; }
`;

export const TopBar = styled.div`
  position: sticky !important;
  top: 0;
  z-index: 20 !important;
  margin: 0 -2em;
  padding: 24px 2em 14px;
  backdrop-filter: saturate(160%) ${T.blurBg};
  -webkit-backdrop-filter: saturate(160%) ${T.blurBg};
  background: ${({ $scrolled, theme }) => ($scrolled ? glass(theme) : "transparent")};
  border-bottom: 1px solid ${({ $scrolled, theme }) => ($scrolled ? hair(theme) : "transparent")};
  transition: background ${T.durBase} ${T.easeOut}, border-color ${T.durBase} ${T.easeOut};

  .title-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  .eyebrow {
    ${eyebrow};
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
    margin-bottom: 6px;
  }

  h1 {
    font-size: 1.7em;
    font-weight: 600;
    letter-spacing: -0.025em;
    line-height: 1.15;
    margin: 0 0 8px;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.78em;
  }

  .update-tag {
    ${mono};
    font-size: 0.9em;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
    &::before { content: "· "; opacity: 0.5; }
  }

  .actions { display: flex; align-items: center; gap: 8px; }

  .nav-row {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
`;

export const LiveDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  ${mono};
  font-size: 0.95em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${T.brand};

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${T.brand};
    box-shadow: 0 0 8px ${T.brandRing};
    animation: ${pulseDot} 2.2s ${T.easeInOut} infinite;
  }
`;

export const IconButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: ${T.radiusMd};
  border: 1px solid ${({ theme }) => hair(theme)};
  background: ${({ theme }) => surf(theme, 1)};
  color: ${({ theme }) => theme.text};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all ${T.durBase} ${T.easeOut};

  svg { opacity: 0.7; }
  &:hover { background: ${({ theme }) => surf(theme, 2)}; border-color: ${({ theme }) => hair(theme, true)}; svg { opacity: 1; } }
  &:active { transform: scale(0.94); }
`;

// ─── Segmented control (thumb deslizante, estado ativo esmeralda) ───────────
export const Segmented = styled.div`
  position: relative;
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  padding: 3px;
  border-radius: ${T.radiusMd};
  background: ${({ theme }) => surf(theme, 0)};
  border: 1px solid ${({ theme }) => hair(theme)};
  min-width: ${({ $min }) => $min || "auto"};

  .thumb {
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: 3px;
    border-radius: ${T.radiusSm};
    background: ${T.brandSoft};
    box-shadow: inset 0 0 0 1px ${T.brandRing};
    transition: transform 0.32s ${T.easeOut}, width 0.32s ${T.easeOut};
  }

  button {
    position: relative;
    z-index: 1;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    ${mono};
    font-size: ${({ $small }) => ($small ? "0.7em" : "0.74em")};
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: ${({ $small }) => ($small ? "6px 11px" : "8px 14px")};
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    transition: opacity ${T.durBase} ${T.easeOut}, color ${T.durBase} ${T.easeOut};

    svg { font-size: 1.1em; }
    &:hover { opacity: 0.85; }
    &.active { opacity: 1; color: ${T.brand}; }

    .count {
      font-size: 0.92em;
      padding: 1px 6px;
      border-radius: ${T.radiusPill};
      background: ${({ theme }) => surf(theme, 2)};
      color: ${({ theme }) => theme.text};
      opacity: 0.75;
    }
    &.active .count { background: ${T.brandSoft}; color: ${T.brand}; opacity: 1; }
  }
`;

// ─── Campo de busca ─────────────────────────────────────────────────────────
export const SearchField = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border-radius: ${T.radiusMd};
  background: ${({ theme }) => surf(theme, 0)};
  border: 1px solid ${({ theme }) => hair(theme)};
  color: ${({ theme }) => label2(theme)};
  flex: 1;
  min-width: 200px;
  max-width: ${({ $max }) => $max || "none"};
  cursor: text;
  transition: border-color ${T.durBase} ${T.easeOut}, box-shadow ${T.durBase} ${T.easeOut};

  &:focus-within {
    border-color: ${T.brandRing};
    box-shadow: 0 0 0 3px ${T.brandSoft};
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};

    &::placeholder { color: ${({ theme }) => label2(theme)}; }
  }

  .clear {
    border: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ theme }) => surf(theme, 2)};
    color: ${({ theme }) => theme.text};
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }
`;

// ─── Seções / listas agrupadas ─────────────────────────────────────────────
export const Section = styled.section`
  margin-top: ${({ $first }) => ($first ? "12px" : "26px")};
  animation: ${fadeUp} 0.4s ${T.easeOut} both;
  animation-delay: ${({ $delay }) => $delay || 0}ms;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 0 2px 10px;

  h2 {
    margin: 0;
    font-size: 0.82em;
    font-weight: 600;
    letter-spacing: -0.01em;
    opacity: 0.8;
  }

  .caption { font-size: 0.76em; color: ${({ theme }) => label2(theme)}; }

  button.link {
    border: none;
    background: none;
    color: ${T.brand};
    ${mono};
    font-size: 0.68em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    gap: 2px;

    &:hover { opacity: 0.75; }
  }
`;

export const GroupLabel = styled.div`
  ${eyebrow};
  color: ${({ theme }) => theme.text};
  padding: 0 4px 8px;
  margin-top: ${({ $first }) => ($first ? 0 : "20px")};
  display: flex;
  justify-content: space-between;
  align-items: center;

  > span { opacity: 0.45; }
  > span:last-child { opacity: 0.32; }

  position: ${({ $sticky }) => ($sticky ? "sticky" : "static")};
  top: ${({ $stickyTop }) => $stickyTop || "0px"};
  z-index: 5;
  ${({ $sticky, theme }) =>
    $sticky &&
    css`
      padding-top: 8px;
      background: ${glass(theme)};
      backdrop-filter: ${T.blurBg};
    `}
`;

export const Group = styled.div`
  ${({ theme }) => cardBase(theme)};
  overflow: hidden;
`;

export const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${({ $dense }) => ($dense ? "9px 16px" : "12px 16px")};
  min-height: ${({ $dense }) => ($dense ? "44px" : "58px")};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: background ${T.durFast} ${T.easeOut};
  animation: ${fadeIn} 0.3s ease both;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    bottom: 0;
    right: 0;
    left: ${({ $inset }) => $inset || "16px"};
    height: 1px;
    background: ${({ theme }) => hair(theme)};
  }

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      &:hover { background: ${surf(theme, 1)}; .chevron { opacity: 0.7; transform: translateX(2px); } }
      &:active { background: ${surf(theme, 2)}; }
    `}

  .main { flex: 1; min-width: 0; }

  .title {
    font-size: 0.88em;
    font-weight: 500;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .subtitle {
    margin-top: 2px;
    font-size: 0.76em;
    color: ${({ theme }) => label2(theme)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .trailing {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    color: ${({ theme }) => label2(theme)};
    ${mono};
    font-size: 0.74em;
  }

  .value { ${mono}; color: ${({ theme }) => theme.text}; opacity: 0.8; font-weight: 600; }

  .chevron {
    opacity: 0.3;
    font-size: 1.2em;
    margin-right: -4px;
    transition: all ${T.durBase} ${T.easeOut};
  }
`;

// Ícone em "badge" tingido (mesmo padrão do wk-badge da PLCom)
export const IconTile = styled.div`
  width: ${({ $size }) => $size || 30}px;
  height: ${({ $size }) => $size || 30}px;
  border-radius: ${({ $size }) => Math.max(7, Math.round(($size || 30) * 0.28))}px;
  background: ${({ $color }) => `${$color}1a`};
  border: 1px solid ${({ $color }) => `${$color}33`};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: ${({ $size }) => Math.round(($size || 30) * 0.48)}px;
`;

export const Pill = styled.span`
  ${mono};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${T.radiusPill};
  font-size: 0.62em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: ${({ $color, theme }) => $color || theme.text};
  background: ${({ $color, theme }) => ($color ? `${$color}1a` : surf(theme, 2))};
  border: 1px solid ${({ $color, theme }) => ($color ? `${$color}40` : hair(theme))};
`;

// ─── Widgets (padrão kpi-card da PLCom) ─────────────────────────────────────
export const WidgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 4}, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1200px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

export const Widget = styled.div`
  position: relative;
  ${({ theme }) => cardBase(theme)};
  padding: 18px 18px 16px;
  overflow: hidden;
  grid-column: span ${({ $span }) => $span || 1};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  animation: ${fadeUp} 0.45s ${T.easeOut} both;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  &:hover { ${({ theme }) => cardHover(theme)}; }
  ${({ $clickable }) => $clickable && css`&:hover { border-color: ${T.brandRing}; transform: translateY(-1px); }`}

  @media (max-width: 1200px) { grid-column: span ${({ $span }) => Math.min($span || 1, 2)}; }
  @media (max-width: 640px) { grid-column: span 1; }

  .w-head {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.72em;
    font-weight: 500;
    letter-spacing: -0.005em;
    color: ${({ theme }) => theme.text};
    margin-bottom: 10px;

    > * { opacity: 0.55; }
    svg { color: ${({ $accent, theme }) => $accent || theme.text}; opacity: ${({ $accent }) => ($accent ? 0.75 : 0.4)}; }
  }

  .w-value {
    ${mono};
    font-size: 1.85em;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: ${({ $accentValue, theme }) => $accentValue || theme.text};
  }

  .w-unit {
    font-size: 0.5em;
    font-weight: 500;
    letter-spacing: 0;
    opacity: 0.5;
    margin-left: 4px;
  }

  .w-foot {
    ${mono};
    margin-top: 8px;
    font-size: 0.66em;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .w-title {
    font-size: 0.95em;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .w-sub {
    font-size: 0.76em;
    color: ${({ theme }) => label2(theme)};
    margin-top: 2px;
  }

  .w-eyebrow {
    ${eyebrow};
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
  }

  /* acento sutil no topo (substitui o "glow") */
  .w-glow {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 2px;
    background: linear-gradient(90deg, ${({ $accent }) => $accent || "transparent"}, transparent 70%);
    opacity: 0.7;
    pointer-events: none;
  }
`;

// ─── Heatmap ────────────────────────────────────────────────────────────────
export const Heatmap = styled.div`
  display: grid;
  grid-template-columns: 32px repeat(24, minmax(0, 1fr));
  gap: 3px;
  ${mono};
  font-size: 0.6em;
  color: ${({ theme }) => label2(theme)};

  .cell {
    aspect-ratio: 1;
    border-radius: 3px;
    background: ${({ theme }) => surf(theme, 2)};
    transition: transform ${T.durFast} ${T.easeOut};

    &:hover { transform: scale(1.3); }
  }

  .day { display: flex; align-items: center; text-transform: uppercase; letter-spacing: 0.04em; }
  .hour { text-align: center; padding-top: 3px; }
`;

// ─── Barras horizontais (ranking) ───────────────────────────────────────────
export const Meter = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => surf(theme, 2)};
  overflow: hidden;
  margin-top: 6px;

  div {
    height: 100%;
    border-radius: 2px;
    background: ${({ $color }) => $color || T.brand};
    transition: width 0.6s ${T.easeOut};
  }
`;

// ─── Cards de entidade (padrão relatorio-card da PLCom) ─────────────────────
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 14px;
`;

export const EntityCard = styled.div`
  position: relative;
  ${({ theme }) => cardBase(theme)};
  padding: 16px 18px;
  cursor: pointer;
  overflow: hidden;
  animation: ${fadeUp} 0.4s ${T.easeOut} both;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  &:hover {
    border-color: ${T.brandRing};
    background: ${({ theme }) => surf(theme, 1)};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => (dk(theme) ? T.shadowMd : "0 6px 20px rgba(15,23,42,0.06)")};
  }
  &:active { transform: scale(0.99); }

  /* fio de cor da entidade no topo */
  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: linear-gradient(90deg, ${({ $color }) => $color}, transparent 75%);
    opacity: 0.8;
    pointer-events: none;
  }

  .head { display: flex; align-items: center; gap: 12px; }

  .name {
    font-weight: 600;
    font-size: 0.92em;
    letter-spacing: -0.015em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sub {
    ${mono};
    font-size: 0.68em;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
    margin-top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 14px;

    .s-value { ${mono}; font-size: 1.1em; font-weight: 600; letter-spacing: -0.02em; }
    .s-label {
      ${eyebrow};
      font-size: 0.6em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      margin-top: 3px;
    }
  }

  .foot {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid ${({ theme }) => hair(theme)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 0.72em;
    color: ${({ theme }) => label2(theme)};
  }
`;

export const AvatarStack = styled.div`
  display: flex;
  align-items: center;

  > * {
    margin-left: -6px;
    box-shadow: 0 0 0 2px ${({ theme }) => solid(theme)};
  }
  > *:first-child { margin-left: 0; }

  .more {
    ${mono};
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${({ theme }) => surf(theme, 2)};
    border: 1px solid ${({ theme }) => hair(theme)};
    color: ${({ theme }) => theme.text};
    font-size: 8px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// ─── Filtros da timeline (chips roláveis) ───────────────────────────────────
export const ChipScroll = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 2px 6px;
  margin-top: 12px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

export const Chip = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: ${T.radiusPill};
  font-family: inherit;
  font-size: 0.76em;
  font-weight: 500;
  cursor: pointer;
  transition: all ${T.durBase} ${T.easeOut};
  color: ${({ $active, $color, theme }) => ($active ? $color || T.brand : theme.text)};
  background: ${({ $active, $color, theme }) => ($active ? `${$color || T.brand}1a` : surf(theme, 0))};
  border: 1px solid ${({ $active, $color, theme }) => ($active ? `${$color || T.brand}55` : hair(theme))};
  opacity: ${({ $active }) => ($active ? 1 : 0.75)};

  svg { font-size: 0.95em; color: ${({ $color }) => $color || "inherit"}; }
  &:hover { opacity: 1; border-color: ${({ $color, theme }) => ($color ? `${$color}55` : hair(theme, true))}; }
  &:active { transform: scale(0.97); }
`;

// ─── Folha lateral com navegação em pilha ──────────────────────────────────
export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: ${({ theme }) => (dk(theme) ? "rgba(0,0,0,0.5)" : "rgba(15,23,42,0.18)")};
  backdrop-filter: blur(2px);
  animation: ${fadeIn} 0.25s ease both;
`;

export const Sheet = styled.aside`
  position: fixed;
  top: 10px;
  right: 10px;
  bottom: 10px;
  z-index: 1001;
  width: min(560px, calc(100vw - 20px));
  border-radius: ${T.radiusXl};
  background: ${({ theme }) => solid(theme)};
  border: 1px solid ${({ theme }) => hair(theme, true)};
  box-shadow: ${({ theme }) => (dk(theme) ? T.shadowLg : "0 24px 64px rgba(15,23,42,0.16)")};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} 0.42s ${T.easeOut} both;
  color: ${({ theme }) => theme.text};

  /* mesmo brilho de fundo da página */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(500px 300px at 50% -10%, ${T.brandSoft}, transparent 70%);
  }
  > * { position: relative; }
`;

export const SheetNav = styled.div`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  height: 52px;
  padding: 0 10px 0 6px;
  background: ${({ $scrolled, theme }) => ($scrolled ? glass(theme) : "transparent")};
  backdrop-filter: ${T.blurBg};
  border-bottom: 1px solid ${({ $scrolled, theme }) => ($scrolled ? hair(theme) : "transparent")};
  z-index: 2;

  .back {
    justify-self: start;
    border: none;
    background: none;
    color: ${T.brand};
    font-family: inherit;
    font-size: 0.86em;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 1px;
    cursor: pointer;
    padding: 6px;
    max-width: 180px;

    span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    svg { font-size: 1.45em; }
    &:hover { opacity: 0.75; }
  }

  .nav-title {
    font-weight: 600;
    font-size: 0.86em;
    opacity: ${({ $scrolled }) => ($scrolled ? 1 : 0)};
    transition: opacity ${T.durBase} ${T.easeOut};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 230px;
  }

  .close { justify-self: end; }
`;

export const SheetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 18px 28px;

  > .page { animation: ${pushIn} 0.36s ${T.easeOut} both; }
`;

export const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 10px 4px;

  h3 {
    margin: 12px 0 4px;
    font-size: 1.3em;
    font-weight: 600;
    letter-spacing: -0.025em;
    line-height: 1.2;
  }

  .hero-sub {
    ${mono};
    font-size: 0.72em;
    color: ${({ theme }) => label2(theme)};
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }
`;

export const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 4}, 1fr);
  ${({ theme }) => cardBase(theme)};
  margin-top: 16px;

  > div {
    padding: 12px 8px;
    text-align: center;
    position: relative;

    &:not(:last-child)::after {
      content: "";
      position: absolute;
      right: 0;
      top: 12px;
      bottom: 12px;
      width: 1px;
      background: ${({ theme }) => hair(theme)};
    }
  }

  .v { ${mono}; font-size: 1.15em; font-weight: 600; letter-spacing: -0.02em; }
  .l { ${eyebrow}; font-size: 0.58em; color: ${({ theme }) => theme.text}; opacity: 0.45; margin-top: 4px; }
`;

export const CodeBlock = styled.pre`
  margin: 0;
  padding: 14px 16px;
  ${mono};
  font-size: 0.72em;
  line-height: 1.6;
  color: ${({ theme }) => theme.text};
  opacity: 0.85;
  white-space: pre-wrap;
  word-break: break-word;
`;

// ─── Estados ────────────────────────────────────────────────────────────────
export const Skeleton = styled.div`
  height: ${({ $h }) => $h || 16}px;
  width: ${({ $w }) => $w || "100%"};
  border-radius: ${({ $r }) => $r || 6}px;
  background: ${({ theme }) =>
    `linear-gradient(90deg, ${surf(theme, 1)} 0px, ${surf(theme, 2)} 80px, ${surf(theme, 1)} 160px)`};
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`;

export const Empty = styled.div`
  padding: 52px 20px;
  text-align: center;
  color: ${({ theme }) => label2(theme)};

  .e-icon {
    width: 52px;
    height: 52px;
    margin: 0 auto 14px;
    border-radius: ${T.radiusMd};
    background: ${({ theme }) => surf(theme, 1)};
    border: 1px solid ${({ theme }) => hair(theme)};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    opacity: 0.7;
  }

  .e-title { font-weight: 600; font-size: 0.92em; color: ${({ theme }) => theme.text}; margin-bottom: 4px; }
  .e-sub { font-size: 0.8em; }
`;

export const LoadMore = styled.button`
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 11px;
  ${({ theme }) => cardBase(theme)};
  color: ${T.brand};
  ${mono};
  font-size: 0.72em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;

  &:hover { border-color: ${T.brandRing}; background: ${T.brandSoft}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;
